import { fetcher } from "../helpers/global";
import { COMMERCE_CART_TOKEN, COMMERCE_WEBFORM_TOKEN } from "../storage/constants";
import { getWithExpiry } from "../helpers/localStorageExpiry";
import { resetWebform } from "./funds.service";

/**
 * Create a pseudo-random cart token for JSONAPI requests to Drupal Commerce.
 *
 * This ensures anonymous cart sessions work before a session state can be
 * negotiated and initialized, particularly for incognito windows and other
 * empty cache scenarios.
 */
function generateCartToken(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result + Math.floor(Date.now() / 1000);
}

/**
 * Get cart token from local storage.
 *
 * This helper will generate a new cart token if none exists to ensure
 * a null cart token is never sent with JSONAPI requests.
 */
export function getCartToken() {
  // Generate a new cart token when a webform token expires.
  if (localStorage.getItem(COMMERCE_WEBFORM_TOKEN) && !getWithExpiry(COMMERCE_WEBFORM_TOKEN)) {
    localStorage.setItem(COMMERCE_CART_TOKEN, generateCartToken(20));
    resetWebform();
  }

  if (!localStorage.getItem(COMMERCE_CART_TOKEN)) {
    localStorage.setItem(COMMERCE_CART_TOKEN, generateCartToken(20));
  }

  return localStorage.getItem(COMMERCE_CART_TOKEN);
}

export function getCartItemsAndActiveFund() {
  const cartToken = getCartToken();

  const data = [
    {
      requestId: "list",
      uri: "/jsonapi/cart/items",
      action: "view",
      headers: {
        "content-type": "application/vnd.api+json",
        "Commerce-Cart-Token": cartToken,
      },
    },
    {
      requestId: "cart",
      uri: "/jsonapi/carts",
      action: "view",
      headers: {
        "content-type": "application/json",
        "Commerce-Cart-Token": cartToken,
      },
    },
  ];

  const headers = {
    "content-type": "application/json",
    "Commerce-Cart-Token": cartToken,
  };

  const params = { _format: "json" };
  const url = "subrequests";
  return fetcher("POST", url, params, headers, data).then((response) => {
    const { list, cart } = response;
    const listItems = JSON.parse(list.body);
    const cartInfo = JSON.parse(cart.body);
    return { listItems, cartInfo };
  });
}

export function createOrderItems(requestData) {
  const cartToken = getCartToken();
  const headers = {
    "content-type": "application/vnd.api+json",
    "Commerce-Cart-Token": cartToken,
  };
  const url = "jsonapi/donate";
  return fetcher("POST", url, null, headers, requestData).then((response) => {
    return response.data;
  });
}

export function updateOrderItem(orderItem, donation_amount) {
  const cartToken = getCartToken();
  // @todo remove order item gremlins
  const orderId = orderItem?.relationships.order_id.data.id;
  if (!orderId || !orderItem) {
    console.error("Tried to update order without orderId");
    return;
  }
  const url = `jsonapi/carts/${orderId}/items/${orderItem?.id}`;
  const data = {
    data: {
      type: orderItem?.type,
      id: orderItem?.id,
      attributes: {
        quantity: 1,
        overridden_unit_price: true,
        unit_price: {
          number: donation_amount,
          currency_code: "USD",
        },
      },
    },
  };

  const headers = {
    "content-type": "application/vnd.api+json",
    "Commerce-Cart-Token": cartToken,
  };

  return fetcher("PATCH", url, null, headers, data);
}

export function updateCart(cartId, attributes) {
  const url = `jsonapi/orders/donation/${cartId}`;
  const data = {
    data: {
      type: "commerce_order--donation",
      id: cartId,
      attributes,
    },
  };

  const headers = {
    "content-type": "application/vnd.api+json",
    accept: "application/vnd.api+json",
  };

  return fetcher("PATCH", url, null, headers, data);
}

export function fetchFundForCart(id) {
  const cartToken = localStorage.getItem(COMMERCE_CART_TOKEN);
  const url = `jsonapi/fund-add-to-cart/${id}`;
  const headers = {
    "content-type": "application/vnd.api+json",
    "Commerce-Cart-Token": cartToken,
  };
  return fetcher("GET", url, null, headers);
}

export function removeCartItems(cartId, itemIds) {
  const cartToken = localStorage.getItem(COMMERCE_CART_TOKEN);
  const headers = {
    accept: "application/vnd.api+json",
    "Commerce-Cart-Token": cartToken,
  };
  const url = `jsonapi/carts/${cartId}/items`;
  const body = {
    data: itemIds.map((itemId) => ({
      type: "order-item--donation",
      id: itemId,
    })),
  };
  return fetcher("delete", url, null, headers, body);
}

export function hasEmptyAmount(items) {
  return !items.every((item, index) => {
    const itemPrice = parseFloat(item.attributes.total_price.number);
    return itemPrice !== 0;
  });
}

export function findFirstEmptyAmount(items) {
  return items.find((item) => {
    const itemPrice = parseFloat(item.attributes.total_price.number);
    return itemPrice === 0;
  });
}
